import React, { createContext, useContext, useState, useEffect } from 'react';

// 4 palettes: Bleu & Or Québec (défaut) + 3 choix utilisateur
export const PALETTES = {
  quebec: {
    key: 'quebec',
    name: { fr: "Bleu & Or Québec", en: "Quebec Blue & Gold" },
    primary: "#fbbf24",
    primaryDark: "#1e40af",
    accent: "#dbeafe",
    text: "#1e3a8a",
    bg: "from-blue-950 via-blue-900 to-slate-900",
    cardBg: "bg-blue-800/30",
    gradient: "from-amber-400 to-amber-500",
    navSolid: "bg-blue-950/95",
    footerBg: "bg-blue-950",
    // Light page variants
    lightPageBg: "#eff6ff",
    lightAccent: "#1e40af",
    lightAccentBg: "bg-blue-100",
    lightAccentBorder: "border-blue-300",
    lightAccentText: "text-blue-800",
    highlightBg: "bg-amber-50",
    highlightBorder: "border-amber-300",
    highlightText: "text-amber-900",
    sectionAccent: "#fbbf24",
  },
  gold: {
    key: 'gold',
    name: { fr: "Or & Ambre", en: "Gold & Amber" },
    primary: "#fbbf24",
    primaryDark: "#b45309",
    accent: "#fef3c7",
    text: "#78350f",
    bg: "from-slate-900 via-stone-900 to-slate-900",
    cardBg: "bg-stone-800/50",
    gradient: "from-amber-400 to-amber-600",
    navSolid: "bg-stone-900/95",
    footerBg: "bg-stone-950",
    lightPageBg: "#fffbeb",
    lightAccent: "#b45309",
    lightAccentBg: "bg-amber-100",
    lightAccentBorder: "border-amber-300",
    lightAccentText: "text-amber-900",
    highlightBg: "bg-amber-50",
    highlightBorder: "border-amber-300",
    highlightText: "text-amber-900",
    sectionAccent: "#d97706",
  },
  forest: {
    key: 'forest',
    name: { fr: "Forêt Boréale", en: "Boreal Forest" },
    primary: "#4ade80",
    primaryDark: "#166534",
    accent: "#dcfce7",
    text: "#14532d",
    bg: "from-slate-900 via-emerald-950 to-slate-900",
    cardBg: "bg-emerald-900/30",
    gradient: "from-emerald-400 to-green-600",
    navSolid: "bg-emerald-950/95",
    footerBg: "bg-emerald-950",
    lightPageBg: "#f0fdf4",
    lightAccent: "#166534",
    lightAccentBg: "bg-emerald-100",
    lightAccentBorder: "border-emerald-300",
    lightAccentText: "text-emerald-800",
    highlightBg: "bg-emerald-50",
    highlightBorder: "border-emerald-300",
    highlightText: "text-emerald-900",
    sectionAccent: "#16a34a",
  },
  blanc: {
    key: 'blanc',
    name: { fr: "Bleu & Blanc", en: "Blue & White" },
    primary: "#60a5fa",
    primaryDark: "#003DA5",
    accent: "#e0eaff",
    text: "#002868",
    bg: "from-[#001a4d] via-[#003DA5] to-[#001a4d]",
    cardBg: "bg-[#003DA5]/30",
    gradient: "from-blue-400 to-blue-700",
    navSolid: "bg-[#001a4d]/95",
    footerBg: "bg-[#001030]",
    lightPageBg: "#eff6ff",
    lightAccent: "#003DA5",
    lightAccentBg: "bg-blue-100",
    lightAccentBorder: "border-blue-300",
    lightAccentText: "text-blue-900",
    highlightBg: "bg-blue-50",
    highlightBorder: "border-blue-300",
    highlightText: "text-blue-900",
    sectionAccent: "#003DA5",
  }
};

export const PALETTE_KEYS = Object.keys(PALETTES);

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const getInitialPalette = () => {
    const saved = localStorage.getItem('piastre-palette');
    if (saved && PALETTES[saved]) return saved;
    return 'quebec'; // Default: Bleu & Or Québec
  };

  const [paletteKey, setPaletteKey] = useState(getInitialPalette);

  useEffect(() => {
    localStorage.setItem('piastre-palette', paletteKey);
  }, [paletteKey]);

  const palette = PALETTES[paletteKey];

  return (
    <ThemeContext.Provider value={{ palette, paletteKey, setPaletteKey, palettes: PALETTES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
